import { FSMNamedState, ObservableFiniteStateMachine, OMQuery, OMReference, OMUniverse } from "@apptivity-lab/firmament-node-sdk"
import { AffAttendeeProfile } from "@firmament-packages/aff"

import { CnxBooking, CnxConsumerProfile, CnxConsumerPurchase, CnxMeeting, CnxMeetingTemplate, CnxSessionWallet } from "@firmament-packages/connectx"
import WltWallet from "@firmament-packages/wallet/WltWallet"
import { startOfDay } from "@lib"

declare namespace CnxMeetingListStateMachine {
    export interface Start extends FSMNamedState {
        name: "start"
    }

    export interface Loading extends FSMNamedState {
        name: "loading"
        meetings: CnxMeeting[]
        query: OMQuery<CnxMeeting>
    }

    export interface Presenting extends FSMNamedState {
        name: "presenting"
        meetings: CnxMeeting[]
        query: OMQuery<CnxMeeting>
    }

    export interface ShowingDetail extends FSMNamedState {
        name: "showingDetail"
        meeting: CnxMeeting
        meetings: CnxMeeting[]
        query: OMQuery<CnxMeeting>
    }

    export interface PerformingBooking extends FSMNamedState {
        name: "performingBooking"
        consumer: CnxConsumerProfile,
        meeting: CnxMeeting
        meetings: CnxMeeting[]
        query: OMQuery<CnxMeeting>
    }

    export interface ShowingMeetingBooked extends FSMNamedState {
        name: "showingMeetingBooked"
        meeting: CnxMeeting
        booking: CnxBooking
        meetings: CnxMeeting[]
        query: OMQuery<CnxMeeting>
    }

    export interface ShowingInsufficientBalance extends FSMNamedState {
        name: "showingInsufficientBalance"
        meeting: CnxMeeting
        meetings: CnxMeeting[]
        query: OMQuery<CnxMeeting>
    }

    export interface ShowingBookingError extends FSMNamedState {
        name: "showingBookingError"
        error: Error
        consumer: CnxConsumerProfile
        meeting: CnxMeeting
        meetings: CnxMeeting[]
        query: OMQuery<CnxMeeting>
    }

    export interface PerformingBookingCancellation extends FSMNamedState {
        name: "performingBookingCancellation"
        consumer: CnxConsumerProfile,
        meeting: CnxMeeting
        meetings: CnxMeeting[]
        query: OMQuery<CnxMeeting>
    }

    export interface ShowingError extends FSMNamedState {
        name: "showingError"
        error: Error
        meetings: CnxMeeting[]
        query: OMQuery<CnxMeeting>
    }

    export type State = Start |
        Loading |
        Presenting |
        ShowingDetail |
        PerformingBooking | ShowingMeetingBooked | ShowingInsufficientBalance | ShowingBookingError |
        PerformingBookingCancellation |
        ShowingError
}

export type CnxMeetingQueryConfiguration = {
    segment: "ALL" | "BOOKED"
    fromDate?: Date,
    toDate?: Date,
    id?: OMReference<CnxMeeting>,
    template?: OMReference<CnxMeetingTemplate>,
    limit?: number
}

export class CnxMeetingListStateMachine extends ObservableFiniteStateMachine<CnxMeetingListStateMachine.State> {
    private queryConfig?: CnxMeetingQueryConfiguration

    constructor() {
        super({ name: "start" })
    }

    async refresh(queryConfig: CnxMeetingQueryConfiguration) {
        let existingMeetings: CnxMeeting[]

        // Keep track of query config
        this.queryConfig = queryConfig

        switch (this.state.name) {
            case "start":
                existingMeetings = []
                break
            case "presenting":
            case "showingMeetingBooked":
            case "performingBookingCancellation":
                existingMeetings = this.state.meetings
                break
            default:
                return
        }

        const attendeeProfile = OMUniverse.shared.currentUser?.user.attendeeProfile()

        let cutOffTime = queryConfig.fromDate ? new Date(queryConfig.fromDate) : new Date()
        cutOffTime.setTime(cutOffTime.getTime() - 2 * 60 * 60 * 1000)

        // If viewing BOOKED segment, don't show "ended" classes by setting
        // fromDate to max(today, queryConfig.fromDate). Alternatively, we
        // can filter out meetings where actual_end_at IS NOT NULL, if the
        // meetings start/end status is synced internally (i.e. via video
        // platform integration).
        const startOfToday = startOfDay(new Date(), attendeeProfile?.timezone || undefined)
        if (queryConfig.segment === "BOOKED" && cutOffTime.getTime() < startOfToday.getTime()) {
            cutOffTime = new Date(Date.now() - 2 * 60 * 60 * 1000)
        }

        try {
            let query: OMQuery<CnxMeeting> = CnxMeeting.query()
            if (queryConfig.segment === "BOOKED" && attendeeProfile) {
                query = new OMQuery(CnxMeeting, "meetings", new OMReference(AffAttendeeProfile, attendeeProfile))
            }

            query
                .include("meetingTemplate")
                .include("meetingTemplate.coverPhoto" as any)
                .include("serviceTier")
                .include("videoRoom")
                .include("coverPhoto")
                .include("provider")
                .include("provider.photo" as any)
                .include("categories")
                .sort("scheduledStartAt", "ASC")
                .limit(queryConfig.limit || 500)

            if (queryConfig.fromDate) {
                // Don't include meetings that have passed
                query.filter(
                    "scheduledStartAt",
                    "moreThanEqual",
                    new Date(Math.max(cutOffTime.getTime(), queryConfig.fromDate.getTime())).toISOString() as any
                )
            }
            if (queryConfig.toDate) {
                query.filter("scheduledEndAt", "lessThanEqual", queryConfig.toDate.toISOString() as any)
                console.log("!*** toDate: ", queryConfig.toDate)
            }

            if (queryConfig.id) {
                query.filter("id", "equals", queryConfig.id.id)
            }
            if (queryConfig.template) {
                query.filter("meetingTemplate", "equals", queryConfig.template.id as any)
            }

            this.state = { name: "loading", meetings: existingMeetings, query }

            // Load meetings
            await query.execute()
            const meetings = query.resultObjects

            if (attendeeProfile) {
                const bookingsQuery = CnxBooking.query()
                    .filter("consumer", "equals", attendeeProfile.id as any)
                    .filter("bookingStatus", "equals", "CONFIRMED")
                    .limit(queryConfig.limit || 500)

                if (queryConfig.fromDate) {
                    bookingsQuery.filter("meeting.scheduledStartAt" as any, "moreThanEqual", startOfDay(queryConfig.fromDate, attendeeProfile.timezone || undefined).toISOString())
                }

                await bookingsQuery.execute()

                meetings.forEach((meeting) => {
                    if (!meeting.bookings) {
                        meeting.bookings = []
                    }
                    meeting.bookings = bookingsQuery.results.filter((booking) => booking.actualObject?.meeting.id == meeting.id)
                    OMUniverse.shared.touch(meeting)
                })
            }

            this.success(query, meetings)
        } catch (error: unknown) {
            if (error instanceof Error) {
                this.fail(error)
            }
        }
    }

    showDetail(meeting: CnxMeeting) {
        switch (this.state.name) {
            case "presenting":
                this.state = { name: "showingDetail", meeting, meetings: this.state.meetings, query: this.state.query }
                break
            default:
                return
        }
    }

    async book(profile: AffAttendeeProfile, meeting: CnxMeeting) {
        switch (this.state.name) {
            case "presenting":
                break
            default:
                return
        }

        this.state = {
            name: "performingBooking",
            consumer: profile,
            meeting,
            meetings: this.state.meetings,
            query: this.state.query
        }

        try {
            // Ensure we have the latest wallet values by loading wallet again
            const wallets = await CnxSessionWallet.query()
                .filter("consumer", "equals", profile.id as any)
                .execute()
                .then((query) => query.resultObjects)

            const wallet = wallets[0]
            profile.sessionWallets = [new OMReference(CnxSessionWallet, wallet)]
            OMUniverse.shared.touch(profile, true)

            // Ensure profile have enough balance
            if (wallet.transactionsAvailableBalance < meeting.servicePriceCents()) {
                this.state = {
                    name: "showingInsufficientBalance",
                    meeting,
                    meetings: this.state.meetings,
                    query: this.state.query
                }
                return
            }

            // Record the purchase transaction
            const purchase = new CnxConsumerPurchase({
                meeting: new OMReference(CnxMeeting, meeting),
                wallet: new OMReference(WltWallet, wallet),
                transactionType: "CREDIT",
                transactionStatus: "APPROVED",
                currency: "SSN",
                amountInCents: meeting.servicePriceCents()
            })

            OMUniverse.shared.touch(purchase)
            await purchase.save()

            // Create booking for consumer for this meeting
            const booking = new CnxBooking({
                consumer: new OMReference(CnxConsumerProfile, profile),
                meeting: new OMReference(CnxMeeting, meeting),
                bookingStatus: "PENDING",
                purchase: new OMReference(CnxConsumerPurchase, purchase),
                paymentCurrency: wallet.currency,
                paymentGrossRateCents: purchase.amountInCents
            })

            OMUniverse.shared.touch(booking)
            await booking.save()

            // Confirm the booking
            await booking.confirm()

            this.state = {
                name: "showingMeetingBooked",
                meeting,
                booking,
                meetings: this.state.meetings,
                query: this.state.query
            }
        } catch (error: unknown) {
            if (error instanceof Error) {
                this.fail(error)
            } else {
                this.fail(new Error("An unknown error has occurred while attempting to place booking."))
            }
        }
    }

    async cancelBooking(profile: AffAttendeeProfile, meeting: CnxMeeting, booking: CnxBooking) {
        switch (this.state.name) {
            case "presenting":
            case "showingMeetingBooked":
                break
            default:
                return
        }

        this.state = {
            name: "performingBookingCancellation",
            consumer: profile,
            meeting,
            meetings: this.state.meetings,
            query: this.state.query
        }

        try {
            await booking.cancel()
            this.success(this.state.query, this.state.meetings)
        } catch (error: unknown) {
            if (error instanceof Error) {
                this.fail(error)
            } else {
                this.fail(new Error("An unknown error has occurred while attempting to cancel booking."))
            }
        }
    }

    back() {
        switch (this.state.name) {
            case "showingMeetingBooked":
            case "showingInsufficientBalance":
            case "showingBookingError":
                this.state = { name: "presenting", meetings: this.state.meetings, query: this.state.query }
                break
            default:
                return
        }
    }

    private success(query: OMQuery<CnxMeeting>, meetings: CnxMeeting[]) {
        switch (this.state.name) {
            case "loading":
                this.state = { name: "presenting", meetings, query }
                break
            case "performingBookingCancellation":
                if (this.queryConfig) {
                    this.refresh(this.queryConfig)
                } else {
                    this.state = { name: "presenting", meetings, query }
                }
                break
            default:
                return
        }
    }

    private fail(error: Error) {
        switch (this.state.name) {
            case "loading":
                this.state = { name: "showingError", error, meetings: this.state.meetings, query: this.state.query }
                break
            case "performingBooking":
            case "performingBookingCancellation":
                this.state = {
                    name: "showingBookingError",
                    error,
                    consumer: this.state.consumer,
                    meeting: this.state.meeting,
                    meetings: this.state.meetings,
                    query: this.state.query
                }
                break
            default:
                return
        }
    }
}
